import {
  MenuItemContent,
  type MenuItemContentProps,
} from '@components/Menu/MenuItemContent.tsx';
import { classNames } from '@utils/classNames.ts';
import { useCallback, useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverHandler } from '../base/Popover';
import { useIsSmallScreen } from '@src/hooks/useMediaQuery';

export const DropdownMenuItem = ({
  dropDownMenuEntries,
  className,
  showLabel,
  ...menuItemProps
}: {
  dropDownMenuEntries: JSX.Element[];
  className?: string;
} & Omit<MenuItemContentProps, 'isActive'>) => {
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const isPhone = useIsSmallScreen();

  const wrapperOnClick = useCallback(() => {
    setSubMenuOpen(false);
  }, []);

  const popupOffset = useMemo(() => {
    return isPhone
      ? { mainAxis: -56, alignmentAxis: 56 }
      : { mainAxis: 8, alignmentAxis: -2 };
  }, [isPhone]);

  const wrappedMenuItems = useMemo(
    () =>
      dropDownMenuEntries.map((entry, index) => {
        const menuElement = entry as { props: { isSubMenuItem?: boolean } };
        if (!menuElement.props.isSubMenuItem) {
          return entry;
        }
        return (
          <div onClick={wrapperOnClick} key={`wrapped-menu-item-${index}`}>
            {entry}
          </div>
        );
      }),
    [dropDownMenuEntries, wrapperOnClick],
  );

  return (
    <Popover
      open={subMenuOpen}
      handler={setSubMenuOpen}
      placement="right-end"
      offset={popupOffset}
    >
      <PopoverHandler>
        <button
          className={classNames(
            'menuitem h-12 group flex items-center cursor-pointer bg-transparent rounded-xl gap-1',
            showLabel ? 'w-full' : '',
            className,
          )}
        >
          {MenuItemContent({
            isActive: subMenuOpen,
            showLabel,
            ...menuItemProps,
          })}
        </button>
      </PopoverHandler>
      <PopoverContent>
        <div className="p-4 gap-2 flex flex-col">{wrappedMenuItems}</div>
      </PopoverContent>
    </Popover>
  );
};
